/**
 * {{assigned_agent}} in a From / Reply-To field (mig 1550). The send path resolves it per recipient to the contact's
 * ASSIGNED support agent (never the day's coverage agent), sent as "<Agent Name> | U.S. LawShield" <agent address>;
 * when the agent can't be used (none
 * assigned, inactive, no email, or the email isn't on an authenticated sending domain) the recipient gets the
 * fallback sender, the send continues, and one alert is raised per flight / step.
 *
 * MIRRORS supabase/functions/_shared/assignedAgentSender.ts (isAssignedAgentToken). The server re-validates; this is
 * only so the form refuses anything else that looks like a variable.
 */
export const ASSIGNED_AGENT_VAR = "{{assigned_agent}}";

export function isAssignedAgentToken(v: unknown): boolean {
  return (
    typeof v === "string" && /^\{\{\s*assigned_agent\s*\}\}$/i.test(v.trim())
  );
}

/**
 * {{assigned_concierge}} (mig 1770, flights only): each recipient gets the email from the concierge of their
 * organization. MIRRORS supabase/functions/_shared/assignedAgentSender.ts (senderVariableOf).
 */
export const ASSIGNED_CONCIERGE_VAR = "{{assigned_concierge}}";
export type SenderVariable = "assigned_agent" | "assigned_concierge";

export function senderVariableOf(v: unknown): SenderVariable | null {
  if (typeof v !== "string") {
    return null;
  }
  const m = /^\{\{\s*(assigned_agent|assigned_concierge)\s*\}\}$/i.exec(
    v.trim(),
  );
  return m ? (m[1].toLowerCase() as SenderVariable) : null;
}

export const SENDER_VARIABLE_TOKEN: Record<SenderVariable, string> = {
  assigned_agent: ASSIGNED_AGENT_VAR,
  assigned_concierge: ASSIGNED_CONCIERGE_VAR,
};

/** Who a variable sends as, for labels ("support agent" / "concierge"). */
export const SENDER_VARIABLE_WHO: Record<SenderVariable, string> = {
  assigned_agent: "support agent",
  assigned_concierge: "concierge",
};

// Same rule as the server (assignedAgentSender.ts isPlainEmail), so an address the form accepts is never dropped there.
const EMAIL_RE =
  /^[^\s@<>()",;:\\[\]]+@[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/;

export type ReplyToCheck =
  | { ok: true; value: string; isVariable: boolean }
  | { ok: false; error: string };

/**
 * A Reply-To field value: blank, a plain address, or exactly {{assigned_agent}} (normalised to that spelling).
 * Any other {{...}} is refused: only {{assigned_agent}} is supported here.
 */
export function checkReplyToValue(
  raw: string,
  opts: { allowConcierge?: boolean } = {},
): ReplyToCheck {
  const v = raw.trim();
  if (!v) {
    return { ok: true, value: "", isVariable: false };
  }
  if (isAssignedAgentToken(v)) {
    return { ok: true, value: ASSIGNED_AGENT_VAR, isVariable: true };
  }
  // {{assigned_concierge}} only where the caller allows it (campaign flights, mig 1770; not sequence steps).
  if (opts.allowConcierge && senderVariableOf(v) === "assigned_concierge") {
    return { ok: true, value: ASSIGNED_CONCIERGE_VAR, isVariable: true };
  }
  const vars = opts.allowConcierge
    ? `${ASSIGNED_AGENT_VAR} or ${ASSIGNED_CONCIERGE_VAR}`
    : ASSIGNED_AGENT_VAR;
  if (v.includes("{{") || v.includes("}}")) {
    return {
      ok: false,
      error: `Only the ${vars} variable can be used here, or a normal email address.`,
    };
  }
  if (!EMAIL_RE.test(v)) {
    return {
      ok: false,
      error: `Enter a valid email address or ${vars}`,
    };
  }
  return { ok: true, value: v, isVariable: false };
}

/** A fallback Reply-To: blank or a plain address (never a variable). */
export function checkFallbackReplyTo(raw: string): ReplyToCheck {
  const v = raw.trim();
  if (!v) {
    return { ok: true, value: "", isVariable: false };
  }
  if (v.includes("{{") || v.includes("}}") || !EMAIL_RE.test(v)) {
    return {
      ok: false,
      error: "The fallback Reply-To must be a normal email address",
    };
  }
  return { ok: true, value: v, isVariable: false };
}

/** Plain-language help shown under the fields. */
export const ASSIGNED_AGENT_HELP =
  'Each recipient gets the email from their assigned support agent, shown as "<Agent Name> | U.S. LawShield" with the agent\'s address. ' +
  "If a recipient has no agent, the agent is inactive, or the agent's email can't be used, that recipient gets the " +
  "fallback instead. The send continues, and you (the creator) and the admins get one alert with the counts.";

/**
 * Typed display names (flight From-name override / Reply-To name, step From name / Reply-To name), owner 2026-11-04.
 * MIRRORS supabase/functions/_shared/assignedAgentSender.ts sanitizeSenderName (the server re-sanitizes at send):
 * double quotes, backticks, angle brackets, backslashes, { }, control characters (incl. CR / LF / tab) and invisible
 * line / bidi formatting characters are removed, whitespace collapsed, cut to SENDER_NAME_MAX. The apostrophe and the
 * pipe are kept ("Carter's Country"). Blank -> "" (no display name: today's behaviour).
 */
export const SENDER_NAME_MAX = 80;

export function sanitizeSenderName(raw: unknown): string {
  if (typeof raw !== "string") {
    return "";
  }
  return raw
    .replace(
      // eslint-disable-next-line no-control-regex
      /[\u0000-\u001f\u007f-\u009f\u200e\u200f\u2028\u2029\u202a-\u202e\u2066-\u2069"`<>\\{}]/g,
      " ",
    )
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, SENDER_NAME_MAX)
    .trim();
}

/** {{assigned_concierge}} help (mig 1770). */
export const ASSIGNED_CONCIERGE_HELP =
  'Each recipient gets the email from the concierge of their organization (set on the organization page), shown as "<Concierge Name> | U.S. LawShield" with the concierge\'s address. ' +
  "If a recipient has no organization with a concierge, their organizations have different concierges, or the concierge is inactive or their email can't be used, that recipient gets the " +
  "fallback instead. The send continues, and you (the creator) and the admins get one alert with the counts.";

export const FROM_NAME_CONCIERGE_HELP =
  'Recipients who get their concierge see the concierge\'s own name ("<Concierge Name> | U.S. LawShield"). This name is used only for recipients who get the fallback sender.';
export const REPLY_TO_NAME_CONCIERGE_HELP =
  'Replies to recipients who get their concierge go to the concierge, shown under the concierge\'s own name ("<Concierge Name> | U.S. LawShield"). This name is used only for the fallback Reply-To.';

/** Help under a typed From name when From can be the agent. */
export const FROM_NAME_AGENT_HELP =
  'Recipients who get their support agent see the agent\'s own name ("<Agent Name> | U.S. LawShield"). This name is used only for recipients who get the fallback sender.';

/** Help under a typed Reply-To name. */
export const REPLY_TO_NAME_HELP =
  "The name shown next to the Reply-To address when someone replies. Leave blank to show the address alone.";
export const REPLY_TO_NAME_AGENT_HELP =
  'Replies to recipients who get their support agent go to the agent, shown under the agent\'s own name ("<Agent Name> | U.S. LawShield"). This name is used only for the fallback Reply-To.';
